import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useJiraIntegrationPage } from '../jira-integration-page.provider'
import { useProjectUsersQuery } from '../../api/use-project-users-query'
import { TypeAheadDropdown } from '../type-ahead-dropdown'
import { transformFormDataToBugtrackerRequest } from '../../../bugtrackers/api/build-api-payload-bugtracker-credentials'
import { JiraFieldDefaultValueProps } from './jira-field-default-value'
import { ProjectUser } from '../../api/shared'
import { JiraFormFieldTypes } from '../jira-form-field-types'
import { JiraIntegrationForm } from '../jira-form-schema'

export const DefaultUserField = (props: JiraFieldDefaultValueProps) => {
  const { field, updateValue, bugtracker } = props
  const { formatMessage } = useIntl()
  const [userSearch, setUserSearch] = useState<string | undefined>(undefined)
  const [dropdownHidden, setDropdownHidden] = useState<boolean>(true)
  const [textInput, setTextInput] = useState<string>(
    field?.value?.displayName ?? ''
  )

  const {
    state: { organizationUuid }
  } = useJiraIntegrationPage()

  const { getValues, watch } = useFormContext<JiraIntegrationForm>()

  const watchProject = watch(JiraFormFieldTypes.PROJECT)

  const { users } = useProjectUsersQuery({
    organizationUuid,
    bugtrackerRequest: transformFormDataToBugtrackerRequest(getValues),
    projectName:
      watchProject && typeof watchProject === 'object' ? watchProject.key : '',
    userSearch,
    bugtrackerId: bugtracker?.bugtracker_id
  })

  const updateSelectedUser = (user?: ProjectUser) => {
    updateValue(user)
    setTextInput(user?.displayName ?? '')
    setDropdownHidden(true)
  }

  useEffect(() => {
    if (users && users.length > 0) {
      if (typeof field.value === 'string' && field.value?.length > 0) {
        const selectedUser: ProjectUser | undefined = users.find(
          (user) => user?.key === field.value
        )
        updateSelectedUser(selectedUser)
      }
    }

    if (userSearch && users && users.length > 0) {
      setDropdownHidden(false)
    }
    if (!users || users.length <= 0) {
      setDropdownHidden(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, bugtracker?.bugtracker_id])

  const onInputChange = (e) => {
    setTextInput(e)
    setUserSearch(e)
  }

  const outsideDropdownActivity = () => {
    // Set input back to previous value, and if `field.value`
    // is a string then find the associated user data
    const userData = users ?? []
    let displayName = field?.value?.displayName ?? ''
    if (typeof field.value === 'string' && field.value?.length > 0) {
      const selectedUser: ProjectUser | undefined = userData.find(
        (user) => user?.key === field.value
      )
      displayName = selectedUser?.displayName
    }
    setTextInput(displayName)
    setDropdownHidden(true)
  }

  const TextInputProps = {
    value: textInput,
    onChange: onInputChange,
    disabled: watchProject === undefined,
    onFocus: () => {
      setTextInput('')
      setDropdownHidden(false)
    },
    placeholder: formatMessage({
      id: 'placeholder_jiraUsers'
    })
  }

  return (
    <TypeAheadDropdown
      TextInputProps={TextInputProps}
      hidden={dropdownHidden}
      values={users?.filter((user) => !!user) ?? []}
      disableUnassignedAndDefault={true}
      onSelectionClicked={updateSelectedUser}
      onOutsideActivity={outsideDropdownActivity}
      itemToString={(user: ProjectUser) => user?.displayName}
      itemToKey={(user) => `${user.accountId}-${user.displayName}`}
      testId="default-user"
    />
  )
}
